/**
 * Acquisti — gli interventi fermi in attesa di ricambi.
 *
 * La domanda della schermata e' "cosa sta bloccando il lavoro adesso", non
 * "quali ordini esistono": una riga per intervento, ordinata per urgenza.
 *
 * Le categorie si appoggiano al CODICE dello stato RDA, non al nome ne'
 * all'id: e' quello che impedisce a uno stato nuovo — o rinominato dalle
 * Impostazioni — di far sparire un intervento dalla lista. Quelli che il
 * codice non sa leggere finiscono in "Da verificare", con il nome dello stato
 * scritto sotto la targhetta.
 */
import { useState } from 'react'
import { Card, Table, Tag, Typography, Segmented, Alert, Tooltip, Empty, Grid, theme } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import type {
  CategoriaFermo, InterventiFermiResponse, InterventoFermo, OrdineAperto,
} from '@/types/acquisti'

const { Text, Title } = Typography

// Stesso ordine del backend, che e' quello di presentazione.
const ORDINE: CategoriaFermo[] = [
  'scoperto', 'offerta_respinta', 'in_lavorazione', 'approvato_non_ordinato',
  'in_ritardo', 'in_arrivo', 'da_verificare',
]

const COLORE: Record<CategoriaFermo, string> = {
  scoperto: 'default',
  offerta_respinta: 'volcano',
  in_lavorazione: 'gold',
  approvato_non_ordinato: 'blue',
  in_ritardo: 'red',
  in_arrivo: 'green',
  // Viola, che nella tavolozza non e' usato da nessun altro stato: chi la vede
  // deve capire subito che non e' una categoria come le altre.
  da_verificare: 'purple',
}

const AcquistiPage = () => {
  const { token } = theme.useToken()
  const navigate = useNavigate()
  const screens = Grid.useBreakpoint()
  const [filtro, setFiltro] = useState<CategoriaFermo | 'tutte'>('tutte')

  // Sotto i 1200px il fornitore sparisce per primo: delle sei colonne e' la
  // meno urgente per chi guarda cosa blocca il lavoro. Le altre cinque
  // rispondono tutte a "quale auto, cosa manca, per quando".
  const mostraFornitore = !!screens.xl

  const { data, isLoading } = useQuery<InterventiFermiResponse>({
    queryKey: ['interventi-fermi'],
    queryFn: async () => (await axiosInstance.get(API_ENDPOINTS.INTERVENTI_FERMI)).data,
    refetchInterval: 60_000,
  })

  const righe = (data?.items ?? []).filter(r => filtro === 'tutte' || r.categoria === filtro)

  /** La colonna consegna: la data, o il motivo per cui non c'e', o un trattino. */
  const consegna = (r: InterventoFermo) => {
    if (r.data_consegna_prevista) {
      const d = dayjs(r.data_consegna_prevista)
      const scaduta = d.isBefore(dayjs(), 'day')
      return (
        <Text style={{ fontSize: 12, color: scaduta ? token.colorError : undefined }}>
          {d.format('DD/MM/YY')}
        </Text>
      )
    }
    if (r.motivo_deroga_data) {
      return (
        <Tooltip title={`Ordinata senza data di consegna: ${r.motivo_deroga_data}`}>
          <Tag
            color="warning"
            style={{
              fontSize: 11, maxWidth: '100%', display: 'inline-block',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              verticalAlign: 'bottom',
            }}
          >
            deroga: {r.motivo_deroga_data}
          </Tag>
        </Tooltip>
      )
    }
    return <Text type="secondary">—</Text>
  }

  // Larghezze fisse su tutte le colonne prevedibili; l'unica senza larghezza e'
  // "Intervento", che si prende lo spazio restante e tronca. Con
  // tableLayout="fixed" e senza scroll orizzontale, la tabella non puo' sfondare
  // il contenitore a nessuna risoluzione: e' il browser a distribuire i pixel,
  // non il contenuto a reclamarli.
  const colonneComuni = [
    {
      title: 'Scheda e veicolo',
      key: 'scheda',
      width: 170,
      render: (_: unknown, r: InterventoFermo) => (
        <div style={{ minWidth: 0 }}>
          <Text strong style={{ fontSize: 12 }}>{r.numero_scheda ?? '—'}</Text>
          <div>
            <Text
              type="secondary"
              style={{ fontSize: 11 }}
              ellipsis={{ tooltip: [r.targa, r.veicolo].filter(Boolean).join(' · ') }}
            >
              {[r.targa, r.veicolo].filter(Boolean).join(' · ') || '—'}
            </Text>
          </div>
        </div>
      ),
    },
    {
      // Nessuna larghezza: e' la colonna elastica. Le descrizioni degli
      // interventi arrivano dal campo libero della scheda e possono essere
      // lunghe una riga o dieci — tronca sempre, il testo intero sta nel
      // tooltip.
      title: 'Intervento',
      key: 'intervento',
      render: (_: unknown, r: InterventoFermo) => (
        <div style={{ minWidth: 0 }}>
          <Text
            style={{ fontSize: 12, width: '100%' }}
            ellipsis={{ tooltip: r.descrizione_intervento ?? undefined }}
          >
            #{r.progressivo ?? '?'} — {r.descrizione_intervento ?? '—'}
          </Text>
          {r.stato_intervento && (
            <div><Text type="secondary" style={{ fontSize: 11 }}>{r.stato_intervento}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: 'Articoli',
      key: 'articoli',
      width: 90,
      render: (_: unknown, r: InterventoFermo) => (
        <Text style={{ fontSize: 12 }}>
          <Text strong style={{ fontSize: 12 }}>{r.articoli_mancanti}</Text>
          {' / '}{r.articoli_richiesti}
        </Text>
      ),
    },
    ...(mostraFornitore ? [{
      title: 'Fornitore',
      dataIndex: 'fornitore',
      width: 150,
      render: (v: string | null) => (
        v
          ? <Text style={{ fontSize: 12, width: '100%' }} ellipsis={{ tooltip: v }}>{v}</Text>
          : <Text type="secondary">—</Text>
      ),
    }] : []),
    {
      title: 'Consegna prevista',
      key: 'consegna',
      width: 170,
      render: (_: unknown, r: InterventoFermo) => consegna(r),
    },
  ]

  /**
   * La riga porta al dettaglio acquisti dell'intervento — non piu' alla scheda
   * lavoro, che era il ponte provvisorio della sessione precedente.
   *
   * Non apre nulla che il filtro avesse chiuso: le righe sono gia' ristrette
   * all'officina di chi guarda, e GET /acquisti/intervento/{id} rifa' lo stesso
   * controllo per conto suo, negando con 403 gli interventi di altre officine.
   */
  const apriDettaglio = (r: InterventoFermo) => {
    navigate(`/acquisti/${r.intervention_id}`)
  }

  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginTop: 0 }}>Acquisti — interventi fermi</Title>

      <Segmented
        style={{ marginBottom: 12 }}
        value={filtro}
        onChange={v => setFiltro(v as CategoriaFermo | 'tutte')}
        options={[
          { label: `Tutte (${data?.total ?? 0})`, value: 'tutte' },
          ...ORDINE.map(c => ({
            label: `${data?.etichette?.[c] ?? c} (${data?.conteggi?.[c] ?? 0})`,
            value: c,
          })),
        ]}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Table<InterventoFermo>
          dataSource={righe}
          rowKey="intervention_id"
          size="small"
          loading={isLoading}
          pagination={false}
          tableLayout="fixed"
          locale={{ emptyText: <Empty description="Nessun intervento fermo" /> }}
          onRow={r => ({
            onClick: () => apriDettaglio(r),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: 'Stato',
              dataIndex: 'categoria',
              width: 170,
              render: (c: CategoriaFermo, r: InterventoFermo) => (
                <div style={{ minWidth: 0 }}>
                  <Tag color={COLORE[c]} style={{ fontSize: 11 }}>
                    {data?.etichette?.[c] ?? c}
                  </Tag>
                  {/* Solo per "Da verificare": il nome dello stato che il
                      codice non ha saputo classificare. Senza, la riga dice
                      che c'e' un problema ma non quale. */}
                  {c === 'da_verificare' && r.stato_offerta && (
                    <div>
                      <Text
                        type="secondary"
                        style={{ fontSize: 11, width: '100%' }}
                        ellipsis={{ tooltip: `Stato dell'offerta: ${r.stato_offerta}` }}
                      >
                        {r.stato_offerta}
                      </Text>
                    </div>
                  )}
                  {/* Stessa logica per "Offerta respinta": la targhetta dice
                      che qualcuno ha detto di no, questa riga dice perche'. */}
                  {c === 'offerta_respinta' && r.motivo_respinta && (
                    <div>
                      <Text
                        type="secondary"
                        style={{ fontSize: 11, width: '100%' }}
                        ellipsis={{ tooltip: `Motivo del rifiuto: ${r.motivo_respinta}` }}
                      >
                        {r.motivo_respinta}
                      </Text>
                    </div>
                  )}
                </div>
              ),
            },
            ...colonneComuni,
          ]}
        />
      </Card>

      {(data?.totale_vaschetta ?? 0) > 0 && (
        <Card
          size="small"
          title={
            <span>
              Ordini aperti su schede chiuse{' '}
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                ({data?.totale_vaschetta})
              </Text>
            </span>
          }
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Non sono lavori fermi"
            description={
              'Sono ordini rimasti in "Ordinata" su schede riconsegnate o annullate. '
              + 'Il lavoro e’ finito, l’ordine no: finche’ resta aperto non lo vede nessuno.'
            }
          />
          <Table<OrdineAperto>
            dataSource={data?.ordini_aperti_schede_chiuse ?? []}
            rowKey="purchase_request_id"
            size="small"
            pagination={false}
            tableLayout="fixed"
            onRow={r => ({
              onClick: () => apriDettaglio(r),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                title: 'Ritardo',
                dataIndex: 'giorni_di_ritardo',
                width: 110,
                render: (g: number | null) =>
                  g == null
                    ? <Text type="secondary">—</Text>
                    : <Tag color={g > 0 ? 'red' : 'default'} style={{ fontSize: 11 }}>{g} giorni</Tag>,
              },
              {
                title: 'Stato scheda',
                dataIndex: 'stato_scheda',
                width: 130,
                render: (v: string | null) => <Tag style={{ fontSize: 11 }}>{v ?? '—'}</Tag>,
              },
              ...colonneComuni,
            ]}
          />
        </Card>
      )}
    </div>
  )
}

export default AcquistiPage
